import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type FC,
  type PropsWithChildren,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "src/contexts/AuthContext";
import { useStore } from "src/stores/store";
import { SidebarState } from "src/stores/global.store";
import { WALKTHROUGH_STEPS } from "src/components/walkthrough/walkthrough-steps";

interface WalkthroughContextValue {
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  startTour: () => void;
  endTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  mounted: boolean;
}

const WalkthroughContext = createContext<WalkthroughContextValue>({
  isActive: false,
  currentStep: 0,
  totalSteps: WALKTHROUGH_STEPS.length,
  startTour: () => {},
  endTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  skipTour: () => {},
  mounted: false,
});

export const useWalkthrough = () => useContext(WalkthroughContext);

const STORAGE_KEY_PREFIX = "walkthrough:completed:";

function getStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function isCompleted(userId: string): boolean {
  try {
    return localStorage.getItem(getStorageKey(userId)) === "true";
  } catch {
    return false;
  }
}

function markCompleted(userId: string | undefined): void {
  if (!userId) return;
  try {
    localStorage.setItem(getStorageKey(userId), "true");
  } catch {
    // localStorage unavailable — graceful degradation
  }
}

export const WalkthroughProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const previousSidebarState = useRef<SidebarState | null>(null);
  const { user } = useAuth();
  const { globalStore } = useStore();
  const location = useLocation();
  const autoLaunched = useRef(false);

  const totalSteps = WALKTHROUGH_STEPS.length;

  const startTour = useCallback(() => {
    previousSidebarState.current = globalStore.sidebarState;
    if (globalStore.sidebarState !== SidebarState.EXPANDED) {
      globalStore.setSidebarState(SidebarState.EXPANDED);
    }
    setCurrentStep(0);
    setIsActive(true);
  }, [globalStore]);

  const restoreSidebar = useCallback(() => {
    if (previousSidebarState.current !== null) {
      globalStore.setSidebarState(previousSidebarState.current);
      previousSidebarState.current = null;
    }
  }, [globalStore]);

  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    restoreSidebar();
    markCompleted(user?.id);
  }, [restoreSidebar, user?.id]);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    restoreSidebar();
    markCompleted(user?.id);
  }, [restoreSidebar, user?.id]);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev >= totalSteps - 1) return prev;
      return prev + 1;
    });
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => {
      if (prev <= 0) return prev;
      return prev - 1;
    });
  }, []);

  // Auto-launch on first dashboard visit
  useEffect(() => {
    if (autoLaunched.current) return;
    if (!user?.id) return;
    if (location.pathname !== "/") return;
    if (isCompleted(user.id)) return;

    autoLaunched.current = true;
    const timer = setTimeout(() => {
      startTour();
    }, 1000);

    return () => clearTimeout(timer);
  }, [user?.id, location.pathname, startTour]);

  // Close tour on route change away from dashboard
  useEffect(() => {
    if (isActive && location.pathname !== "/") {
      setIsActive(false);
      setCurrentStep(0);
      restoreSidebar();
    }
  }, [location.pathname, isActive, restoreSidebar]);

  return (
    <WalkthroughContext.Provider
      value={{
        isActive,
        currentStep,
        totalSteps,
        startTour,
        endTour,
        nextStep,
        prevStep,
        skipTour,
        mounted: true,
      }}
    >
      {children}
    </WalkthroughContext.Provider>
  );
};
